import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import ClientDropdown from '../components/ClientDropdown';
import { lookupGst, isValidGstin } from '../services/gstApi';
import { getColours, isColourAllowed, createColour, deleteColour, Colour } from '../services/colourApi';
import { getYarnNames, isYarnNameAllowed, createYarnName as createYarnNameApi, deleteYarnName } from '../services/yarnNameApi';
import { useCanCreate, useCanUpdate, useCanDelete } from '../components/Can';
import { useAuth } from '../contexts/AuthContext';
import YarnLabelPrint from '../components/YarnLabelPrint';
import { useCompanyScope } from '../hooks/useCompanyScope';
import CompanyCell from '../components/CompanyCell';
import CompanySelect from '../components/CompanySelect';

// ─── Field visibility config (per-user, saved on the server) ───
// A company admin / user can pick which fields show on the Yarn Master entry form.
const YM_HEADER_FIELDS: { key: string; label: string; required?: boolean }[] = [
  { key: 'buyerName', label: 'Buyer', required: true },
  { key: 'challanNo', label: 'Challan No' },
  { key: 'receivedDate', label: 'Date' },
  { key: 'vehicleNo', label: 'Vehicle No' },
  { key: 'yarnName', label: 'Item Name', required: true },
  { key: 'hsnCode', label: 'HSN Code' },
  { key: 'color', label: 'Color' },
  { key: 'colorCode', label: 'Color Code' },
  { key: 'supplier', label: 'Supplier / Mill' },
  { key: 'lot', label: 'Lot' },
  { key: 'grade', label: 'Grade' },
];
const YM_TABLE_COLUMNS: { key: string; label: string }[] = [
  { key: 'crtnNo', label: 'Crtn No' },
  { key: 'twist', label: 'Twist' },
  { key: 'cheese', label: 'Chs.' },
  { key: 'grossWeight', label: 'GrWt' },
  { key: 'tareWeight', label: 'TrWt' },
  { key: 'netWeight', label: 'NetWt' },
  { key: 'location', label: 'Location' },
];
const YM_ALL_FIELDS = [...YM_HEADER_FIELDS, ...YM_TABLE_COLUMNS];
const YM_REQUIRED_KEYS = YM_HEADER_FIELDS.filter(f => f.required).map(f => f.key);

// Columns of the Yarn Inward LIST table (separate from the entry-form fields above).
// The user chooses which of these show, via the list's own "Columns" chooser.
const YM_LIST_COLUMNS: { key: string; label: string; required?: boolean }[] = [
  { key: 'item', label: 'Item', required: true },
  { key: 'buyer', label: 'Buyer' },
  { key: 'supplier', label: 'Supplier' },
  { key: 'lot', label: 'Lot' },
  { key: 'crtn', label: 'Crtn' },
  { key: 'crtnNo', label: 'Crtn No' },
  { key: 'cheese', label: 'Cheese' },
  { key: 'grossWeight', label: 'Gr.Wt' },
  { key: 'tareWeight', label: 'Tr.Wt' },
  { key: 'netWeight', label: 'Net Wt' },
  { key: 'location', label: 'Location' },
  { key: 'date', label: 'Date' },
  { key: 'status', label: 'Status' },
];
const YM_LIST_REQUIRED = YM_LIST_COLUMNS.filter(c => c.required).map(c => c.key);

// Searchable Location dropdown: lists warehouse bins (empty ones first) with their box count.
// Renders all options by default; type to filter; Arrow Up/Down move the highlight; Enter picks the
// highlighted bin (free text allowed) and calls onEnter so the grid can advance to the next row.
const LocationDropdown: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; boxes: number }[];
  onEnter?: () => void;
}> = ({ value, onChange, options, onEnter }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 200 });
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      // right-align the panel to the input so it stays on screen in the far-right Location column
      setDropdownPos({ top: rect.bottom + 2, left: Math.max(8, rect.right - 200), width: 200 });
    }
  }, [isOpen]);

  const term = (isOpen ? search : value).toUpperCase();
  const filtered = options.filter(o => o.value.includes(term));
  const visible = filtered.slice(0, 80);

  // Keep the highlight in range as the filtered list shrinks, and scroll it into view as it moves
  useEffect(() => { setActiveIndex(i => Math.min(Math.max(i, 0), Math.max(visible.length - 1, 0))); }, [visible.length]);
  useEffect(() => { if (isOpen) activeItemRef.current?.scrollIntoView({ block: 'nearest' }); }, [activeIndex, isOpen]);

  const commit = (v: string) => { onChange(v.toUpperCase().trim()); setSearch(''); setIsOpen(false); inputRef.current?.blur(); };
  const openDropdown = () => { setIsOpen(true); setSearch(value); setActiveIndex(0); };
  // Enter: pick the highlighted bin (or the typed text), then let the grid add/advance to the next row
  const choose = () => { commit(visible[activeIndex]?.value || term); onEnter?.(); };

  return (
    <div className="relative" ref={ref}>
      <input ref={inputRef} type="text" value={isOpen ? search : value}
        onChange={e => { setSearch(e.target.value.toUpperCase()); setActiveIndex(0); if (!isOpen) setIsOpen(true); }}
        onFocus={openDropdown}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); choose(); }
          else if (e.key === 'Escape') { setIsOpen(false); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); if (!isOpen) openDropdown(); else setActiveIndex(i => Math.min(i + 1, visible.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
        }}
        placeholder="A-1"
        className="w-full px-2 py-1 text-sm text-center font-medium text-[#0055a9] border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent uppercase" />
      {isOpen && createPortal(
        <div ref={dropdownRef} className="fixed z-[9999] bg-white border border-[#E1E6EF] rounded-xl shadow-[0px_4px_16px_rgba(0,0,0,0.1)] max-h-56 overflow-y-auto"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}>
          {visible.length > 0 ? visible.map((o, idx) => (
            <button key={o.value} type="button" tabIndex={-1}
              ref={idx === activeIndex ? activeItemRef : undefined}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => commit(o.value)}
              className={`w-full flex items-center justify-between px-3 py-1.5 text-sm transition-colors ${idx === activeIndex ? 'bg-[#EEF1FB]' : ''} ${value === o.value ? 'text-[#0055a9] font-semibold' : 'text-[#0E131B]'}`}>
              <span className="tabular-nums">{o.value}</span>
              {o.boxes > 0
                ? <span className="text-[10px] font-semibold text-orange-500">{o.boxes} box</span>
                : <span className="text-[10px] font-semibold text-emerald-600">empty</span>}
            </button>
          )) : <div className="px-3 py-2 text-xs text-[rgba(14,19,27,0.4)]">Press Enter to use “{term}”</div>}
        </div>,
        document.body
      )}
    </div>
  );
};

// Searchable Colour-master dropdown: type to filter by name or code, Arrow Up/Down + Enter to pick.
// Replaces a plain native <select> of colours. Unlike a native <select> — whose <option value> is a
// single string, so two colours sharing a name (different dye-lot codes) collapse onto the same value
// and picking either one resolves back to whichever comes first in the list — this commits the exact
// Colour object the user clicked, so same-name/different-code entries never get confused.
const ColourDropdown: React.FC<{
  colours: Colour[];
  display: string;
  isSelected: (c: Colour) => boolean;
  onPick: (c: Colour) => void;
  placeholder?: string;
  className: string;
}> = ({ colours, display, isSelected, onPick, placeholder, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ top: 0, left: 0, width: 240 });
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!isOpen || !inputRef.current) return;
    const reposition = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      const shouldOpenUp = window.innerHeight - rect.bottom < 220 && rect.top > 220;
      setOpenUp(shouldOpenUp);
      const width = Math.min(Math.max(rect.width, 240), window.innerWidth - 16);
      setDropdownPos({
        top: shouldOpenUp ? undefined : rect.bottom + 2,
        bottom: shouldOpenUp ? (window.innerHeight - rect.top + 2) : undefined,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        width,
      });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen]);

  const term = (isOpen ? search : display).trim().toLowerCase();
  const filtered = term
    ? colours.filter((c) => c.name.toLowerCase().includes(term) || (c.code || '').toLowerCase().includes(term))
    : colours;

  useEffect(() => { setActiveIndex(0); }, [term, isOpen]);
  useEffect(() => { if (isOpen) activeItemRef.current?.scrollIntoView({ block: 'nearest' }); }, [activeIndex, isOpen]);

  const commit = (c: Colour) => { onPick(c); setSearch(''); setIsOpen(false); inputRef.current?.blur(); };
  const openDropdown = () => { setIsOpen(true); setSearch(''); setActiveIndex(0); };

  return (
    <div className="relative" ref={ref}>
      <input ref={inputRef} type="text" value={isOpen ? search : display}
        onChange={e => { setSearch(e.target.value); if (!isOpen) setIsOpen(true); }}
        onFocus={openDropdown}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); if (filtered[activeIndex]) commit(filtered[activeIndex]); }
          else if (e.key === 'Escape') { setIsOpen(false); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); if (!isOpen) openDropdown(); else setActiveIndex(i => Math.min(i + 1, filtered.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
        }}
        placeholder={placeholder}
        className={className} />
      {isOpen && createPortal(
        <div ref={dropdownRef} className="fixed z-[9999] bg-white border border-[#E1E6EF] rounded-xl shadow-[0px_4px_16px_rgba(0,0,0,0.1)] max-h-56 overflow-y-auto"
          style={{ ...(openUp ? { bottom: dropdownPos.bottom } : { top: dropdownPos.top }), left: dropdownPos.left, width: dropdownPos.width }}>
          {filtered.length > 0 ? filtered.map((c, idx) => (
            <button key={c.id} type="button" tabIndex={-1}
              ref={idx === activeIndex ? activeItemRef : undefined}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => commit(c)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-left transition-colors ${idx === activeIndex ? 'bg-[#EEF1FB]' : ''} ${isSelected(c) ? 'text-[#0055a9] font-semibold' : 'text-[#0E131B]'}`}>
              <span className="truncate">{c.name}</span>
              {c.code && <span className="shrink-0 text-xs text-[rgba(14,19,27,0.45)]">{c.code}</span>}
            </button>
          )) : <div className="px-3 py-2 text-xs text-[rgba(14,19,27,0.4)]">No matches</div>}
        </div>,
        document.body
      )}
    </div>
  );
};

interface CartonRow {
  crtnNo: string;
  twist: string;
  cheese: string;
  grossWeight: string;
  tareWeight: string;
  netWeight: string;
  location: string;
}

const emptyCartonRow = (crtnNo = ''): CartonRow => ({
  crtnNo, twist: '', cheese: '', grossWeight: '', tareWeight: '', netWeight: '', location: ''
});

const isRowEmpty = (r: CartonRow) =>
  !r.grossWeight && !r.tareWeight && !r.netWeight;

// Shrink a phone photo before OCR upload: cap the longest side and re-encode as JPEG.
// A 5–8 MB photo becomes a few hundred KB → much faster upload AND faster OCR (Gemini caps
// image resolution anyway). Falls back to the original file on any error / if already small.
async function downscaleImage(file: File, maxDim = 2000, quality = 0.9): Promise<Blob> {
  try {
    if (!file.type.startsWith('image/')) return file;
    const bitmap = await createImageBitmap(file);
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = Math.min(1, maxDim / longest);
    if (scale === 1 && file.size < 1_500_000) { bitmap.close?.(); return file; }
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/jpeg', quality));
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

const YarnMaster: React.FC = () => {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'list' | 'add' | 'edit'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Add form: header fields
  const [headerData, setHeaderData] = useState({
    buyerName: '', buyerClientId: null as number | null,
    challanNo: '', receivedDate: new Date().toISOString().split('T')[0],
    vehicleNo: '', yarnName: '', hsnCode: '', lot: '', grade: '', color: '', colorCode: '', supplier: '', supplierGst: '',
    packagingType: 'box', startCrtnNo: '1'
  });

  // GST auto-fill: look up the supplier GSTIN and pre-fill the supplier name.
  const [gstLoading, setGstLoading] = useState(false);
  // When the supplier GST comes from a challan scan we hide the Fetch button (already captured);
  // it's only needed when the user types the GST by hand. Manual editing flips this back to false.
  const [supplierGstFromScan, setSupplierGstFromScan] = useState(false);
  const handleFetchSupplierGst = async () => {
    const gstin = (headerData.supplierGst || '').trim();
    if (!gstin) { toast.error('Enter the supplier GST number first'); return; }
    setGstLoading(true);
    try {
      const d = await lookupGst(gstin);
      setHeaderData(p => ({ ...p, supplier: d.name || p.supplier, supplierGst: d.gstin }));
      toast.success(`Found: ${d.name || d.legalName}${d.status ? ` (${d.status})` : ''}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'GST lookup failed');
    } finally {
      setGstLoading(false);
    }
  };

  // Manual overrides for summary badges
  const [manualCrtn, setManualCrtn] = useState<string | null>(null);
  const [manualNetWt, setManualNetWt] = useState<string | null>(null);

  // Add form: carton rows
  const [rows, setRows] = useState<CartonRow[]>([emptyCartonRow('1')]);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const setInputRef = useCallback((rowIndex: number, field: string, el: HTMLInputElement | null) => {
    inputRefs.current[`${rowIndex}-${field}`] = el;
  }, []);

  // OCR scan state
  const [isScanning, setIsScanning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // List view state
  const [actionMenuId, setActionMenuId] = useState<number | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // ─── Per-user field visibility (persisted on the server, follows the account) ───
  const [showFieldSettings, setShowFieldSettings] = useState(false);
  const buildVisible = (saved?: Record<string, boolean> | null) => {
    const next: Record<string, boolean> = {};
    YM_ALL_FIELDS.forEach(f => {
      // required fields are always visible; others honor the saved choice (default: shown)
      next[f.key] = YM_REQUIRED_KEYS.includes(f.key) ? true : (saved ? saved[f.key] !== false : true);
    });
    return next;
  };
  const [visibleFields, setVisibleFields] = useState<Record<string, boolean>>(() => buildVisible(null));
  const isVisible = (key: string) => visibleFields[key] !== false;

  // Load this user's saved preferences from the server
  const { data: prefsResp } = useQuery({
    queryKey: ['userPreferences'],
    queryFn: async () => await apiService.get('/auth/preferences'),
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (prefsResp) {
      setVisibleFields(buildVisible(prefsResp?.preferences?.yarnMasterFields));
      setVisibleListCols(buildVisibleList(prefsResp?.preferences?.yarnMasterListColumns));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsResp]);

  // Persist changes to the server so they survive logout / new browsers
  const savePrefsMutation = useMutation({
    mutationFn: async (fields: Record<string, boolean>) =>
      await apiService.put('/auth/preferences', { yarnMasterFields: fields }),
    onError: () => toast.error('Could not save field settings'),
  });
  const toggleField = (key: string) => {
    if (YM_REQUIRED_KEYS.includes(key)) return; // required fields stay visible
    const next = { ...visibleFields, [key]: !(visibleFields[key] !== false) };
    setVisibleFields(next);
    savePrefsMutation.mutate(next);
  };
  const resetFields = () => {
    const all = buildVisible(null);
    setVisibleFields(all);
    savePrefsMutation.mutate(all);
  };

  // ─── Per-user LIST column visibility (separate from the entry-form field settings above) ───
  const [showListSettings, setShowListSettings] = useState(false);
  const buildVisibleList = (saved?: Record<string, boolean> | null) => {
    const next: Record<string, boolean> = {};
    YM_LIST_COLUMNS.forEach(c => {
      next[c.key] = YM_LIST_REQUIRED.includes(c.key) ? true : (saved ? saved[c.key] !== false : true);
    });
    return next;
  };
  const [visibleListCols, setVisibleListCols] = useState<Record<string, boolean>>(() => buildVisibleList(null));
  const isListVisible = (key: string) => visibleListCols[key] !== false;
  const saveListPrefsMutation = useMutation({
    mutationFn: async (cols: Record<string, boolean>) =>
      await apiService.put('/auth/preferences', { yarnMasterListColumns: cols }),
    onError: () => toast.error('Could not save column settings'),
  });
  const toggleListColumn = (key: string) => {
    if (YM_LIST_REQUIRED.includes(key)) return; // Item stays visible (row identifier)
    const next = { ...visibleListCols, [key]: !(visibleListCols[key] !== false) };
    setVisibleListCols(next);
    saveListPrefsMutation.mutate(next);
  };
  const resetListColumns = () => {
    const all = buildVisibleList(null);
    setVisibleListCols(all);
    saveListPrefsMutation.mutate(all);
  };
  // Distinct warehouse bins occupied by a lot's cartons (for the list Location column).
  const lotLocations = (entry: any): string => {
    const locs = Array.from(new Set((entry.items || []).map((it: any) => String(it.location || '').trim()).filter(Boolean)));
    return locs.join(', ');
  };
  // Every carton number of a lot, in entry order — shown in one cell, truncated with the full list on hover.
  const lotCrtnNos = (entry: any): string =>
    (entry.items || []).slice().sort((a: any, b: any) => a.srNo - b.srNo)
      .map((it: any) => String(it.crtnNo || '').trim()).filter(Boolean).join(', ');
  const { user } = useAuth() as { user: any };
  const { showCompanyColumn } = useCompanyScope();
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  // Number of visible list columns (+1 for the trailing actions column) — for expanded-row colSpan.
  const visibleListColSpan = YM_LIST_COLUMNS.filter(c => isListVisible(c.key)).length + 1 + (showCompanyColumn ? 1 : 0);

  // ─── Location dropdown options: warehouse bins from the dashboard grid config, empty ones first ───
  const { data: locDashResp } = useQuery({
    queryKey: ['yarnDashboard'],
    queryFn: async () => await apiService.get('/yarn-masters/dashboard'),
    refetchOnWindowFocus: false,
  });
  const locationOptions = useMemo(() => {
    const occ: Record<string, number> = {};
    (locDashResp?.bins || []).forEach((b: any) => { occ[String(b.location).toUpperCase()] = b.totalBoxes; });
    // Count cartons placed in the current (unsaved) form so each bin reflects the boxes we're adding now
    const pending: Record<string, number> = {};
    rows.forEach(r => {
      const loc = r.location?.trim().toUpperCase();
      if (loc) pending[loc] = (pending[loc] || 0) + 1;
    });
    const boxesAt = (v: string) => (occ[v] || 0) + (pending[v] || 0);
    const cfg = (locDashResp as any)?.dashboardConfig; // company-wide layout (same source as the dashboard)
    const sections: string[] = Array.isArray(cfg?.groups) ? cfg.groups.flat()
      : (Array.isArray(cfg?.sections) ? cfg.sections : ['D', 'C', 'B', 'A']);
    const binsPer = Math.min(60, Math.max(1, Number(cfg?.binsPerSection) || 15));
    const opts: { value: string; boxes: number }[] = [];
    const seen = new Set<string>();
    sections.forEach(sec => {
      for (let n = 1; n <= binsPer; n++) {
        const v = `${String(sec).toUpperCase()}-${n}`;
        if (!seen.has(v)) { seen.add(v); opts.push({ value: v, boxes: boxesAt(v) }); }
      }
    });
    // include any occupied bins (saved or pending) that fall outside the configured grid
    [...Object.keys(occ), ...Object.keys(pending)].forEach(v => {
      if (!seen.has(v)) { seen.add(v); opts.push({ value: v, boxes: boxesAt(v) }); }
    });
    return opts.sort((a, b) => (a.boxes > 0 ? 1 : 0) - (b.boxes > 0 ? 1 : 0)); // empty first (stable)
  }, [locDashResp, prefsResp, rows]);

  // Queries
  const { data, isLoading } = useQuery({
    queryKey: ['yarn-masters', page, pageSize, searchQuery, statusFilter, filterDate],
    queryFn: () => apiService.get(`/yarn-masters?page=${page}&pageSize=${pageSize}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}${statusFilter ? `&status=${statusFilter}` : ''}${filterDate ? `&startDate=${filterDate}&endDate=${filterDate}` : ''}`)
  });
  const entries = data?.entries || [];
  const pagination = data?.pagination || { total: 0, totalPages: 0 };

  // Buyer dropdown is locked to approved buyer parties (falls back to all clients for a
  // tenant that hasn't curated a buyer list yet — handled server-side).
  const { data: clients = [] } = useQuery<any[]>({
    queryKey: ['clients', 'buyers'],
    queryFn: async () => { const d = await apiService.get('/clients?buyer=true'); return d.clients || []; },
    select: (d: any) => (Array.isArray(d) ? d : (d?.clients || [])),
  });

  // Admin-managed colour master — the entered/scanned colour + code must be in it (unless empty).
  const { data: colours = [] } = useQuery({ queryKey: ['colours'], queryFn: getColours });
  // OWNER is the tenant owner (shown as "Admin" in the UI) — treat it as admin-level too.
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN' || user?.role === 'OWNER';
  const [colourModalOpen, setColourModalOpen] = useState(false);
  const [newColourName, setNewColourName] = useState('');
  const [newColourCode, setNewColourCode] = useState('');
  const addColourMutation = useMutation({
    mutationFn: () => createColour({ name: newColourName.trim(), code: newColourCode.trim() || undefined }),
    onSuccess: () => { toast.success('Colour added'); setNewColourName(''); setNewColourCode(''); queryClient.invalidateQueries({ queryKey: ['colours'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to add colour'),
  });
  const removeColourMutation = useMutation({
    mutationFn: (id: number) => deleteColour(id),
    onSuccess: () => { toast.success('Colour removed'); queryClient.invalidateQueries({ queryKey: ['colours'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to remove colour'),
  });

  // Admin-managed yarn/item-name master — the entered/scanned Item Name must be in it (unless empty).
  const { data: yarnNames = [] } = useQuery({ queryKey: ['yarnNames'], queryFn: getYarnNames });
  const [yarnNameModalOpen, setYarnNameModalOpen] = useState(false);
  const [newYarnMasterName, setNewYarnMasterName] = useState('');
  const addYarnNameMutation = useMutation({
    mutationFn: () => createYarnNameApi({ name: newYarnMasterName.trim() }),
    onSuccess: () => { toast.success('Yarn name added'); setNewYarnMasterName(''); queryClient.invalidateQueries({ queryKey: ['yarnNames'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to add yarn name'),
  });
  const removeYarnNameMutation = useMutation({
    mutationFn: (id: number) => deleteYarnName(id),
    onSuccess: () => { toast.success('Yarn name removed'); queryClient.invalidateQueries({ queryKey: ['yarnNames'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to remove yarn name'),
  });

  const createMutation = useMutation({
    mutationFn: (d: any) => apiService.post('/yarn-masters', d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['yarn-masters'] });
      toast.success('Yarn entry saved');
      setMode('list');
      resetForm();
    },
    onError: () => toast.error('Failed to save')
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.delete(`/yarn-masters/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['yarn-masters'] }); toast.success('Deleted'); },
    onError: () => toast.error('Failed to delete')
  });

  // Header-only edit: PUT without `items`, so the backend leaves every carton (and its
  // barcode / already-printed label) untouched — see updateYarnMaster's `if (items)` guard.
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiService.put(`/yarn-masters/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['yarn-masters'] });
      toast.success('Yarn entry updated');
      setEditingId(null);
      setMode('list');
    },
    onError: () => toast.error('Failed to update')
  });

  // Close action menu on outside click
  useEffect(() => {
    if (actionMenuId === null) return;
    const handler = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) setActionMenuId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [actionMenuId]);

  const resetForm = () => {
    setHeaderData({
      buyerName: '', buyerClientId: null, challanNo: '',
      receivedDate: new Date().toISOString().split('T')[0],
      vehicleNo: '', yarnName: '', hsnCode: '', lot: '', grade: '', color: '', colorCode: '', supplier: '', supplierGst: '',
      packagingType: 'box', startCrtnNo: '1'
    });
    setRows([emptyCartonRow('1')]);
    setManualCrtn(null);
    setManualNetWt(null);
    setSupplierGstFromScan(false); // fresh manual entry — allow Fetch
    setSelectedTenantId(null); // reopening/after a successful save can't silently reuse the last company
  };

  const canCreate = useCanCreate('yarn-inward');   // gates New Entry / Add / Scan
  const canUpdate = useCanUpdate('yarn-inward');   // gates Edit
  const canDelete = useCanDelete('yarn-inward');   // gates Delete
  const [printEntryId, setPrintEntryId] = useState<number | null>(null);
  const openAdd = () => { resetForm(); setMode('add'); };

  // Open the full-page edit view, prefilled from the row. `receivedDate` is sliced from any
  // ISO string to keep the date input's YYYY-MM-DD format without a timezone shift.
  const openEdit = (id: number) => {
    const entry = entries.find((e: any) => e.id === id);
    if (!entry) { toast.error('Entry not found'); return; }
    setHeaderData({
      buyerName: entry.buyerName || '',
      buyerClientId: entry.buyerClientId ?? null,
      challanNo: entry.challanNo || '',
      receivedDate: entry.receivedDate ? String(entry.receivedDate).split('T')[0] : '',
      vehicleNo: entry.vehicleNo || '',
      yarnName: entry.yarnName || '',
      hsnCode: entry.hsnCode || '',
      lot: entry.lot || '',
      grade: entry.grade || '',
      color: entry.color || '',
      colorCode: entry.colorCode || '',
      supplier: entry.supplier || '',
      supplierGst: entry.supplierGst || '',
      packagingType: entry.packagingType || 'box',
      startCrtnNo: '1',
    });
    setEditingId(id);
    setMode('edit');
    setSupplierGstFromScan(false); // editing a saved record — allow Fetch to re-verify
  };

  // OCR: scan challan image
  const handleScanChallan = async (file: File) => {
    setIsScanning(true);
    try {
      const optimized = await downscaleImage(file); // shrink before upload for a faster scan
      const formPayload = new FormData();
      formPayload.append('image', optimized, 'challan.jpg');

      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:6002/api';
      const response = await fetch(`${apiBase}/ocr/scan-yarn-challan`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formPayload
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.message || 'Scan failed');
      }
      const result = await response.json();
      const d = result.data;

      if (d) {
        // Fill header
        setHeaderData(prev => ({
          ...prev,
          buyerName: d.buyerName || prev.buyerName,
          challanNo: d.challanNo || prev.challanNo,
          receivedDate: d.receivedDate || prev.receivedDate,
          vehicleNo: d.vehicleNo || prev.vehicleNo,
          yarnName: d.yarnName || prev.yarnName,
          hsnCode: d.hsnCode || prev.hsnCode,
          lot: d.lot || prev.lot,
          grade: d.grade || prev.grade,
          color: d.color || prev.color,
          colorCode: d.colorCode || prev.colorCode,
          supplier: d.supplier || prev.supplier,
          supplierGst: d.supplierGst || prev.supplierGst
        }));
        // A scanned GST is already captured from the challan — no manual Fetch needed for it.
        if (d.supplierGst) setSupplierGstFromScan(true);

        // Fill carton rows
        if (d.items && d.items.length > 0) {
          const scannedRows: CartonRow[] = d.items.map((item: any) => ({
            crtnNo: String(item.crtnNo || ''),
            twist: item.twist || '',
            cheese: String(item.cheese ?? ''),
            grossWeight: String(item.grossWeight ?? ''),
            tareWeight: String(item.tareWeight ?? ''),
            netWeight: String(item.netWeight ?? ''),
            location: '' // not scanned — user selects this per row after data loads
          }));
          setRows(scannedRows);
        }

        toast.success(`Scanned ${d.items?.length || 0} rows from challan`);
      }
    } catch (error: any) {
      console.error('Scan error:', error);
      toast.error(error.message || 'Failed to scan challan image');
    } finally {
      setIsScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Enter/arrow navigation order. netWeight is auto-calculated (gross − tare) so it's excluded —
  // pressing Enter on the last visible field (usually tareWeight) adds a new row, like taka entry.
  // Hidden fields are skipped so navigation never gets stuck on a column the user turned off.
  const fieldOrder = useMemo(
    () => (['crtnNo', 'twist', 'cheese', 'grossWeight', 'tareWeight'] as const).filter(f => visibleFields[f] !== false),
    [visibleFields]
  );

  const updateRow = useCallback((index: number, field: keyof CartonRow, value: string) => {
    setRows(prev => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };

      // Auto-calculate net weight = gross - tare
      if (field === 'grossWeight' || field === 'tareWeight') {
        const gw = parseFloat(field === 'grossWeight' ? value : updated.grossWeight) || 0;
        const tw = parseFloat(field === 'tareWeight' ? value : updated.tareWeight) || 0;
        if (gw > 0) {
          updated.netWeight = (gw - tw).toFixed(2);
        }
      }

      next[index] = updated;
      return next;
    });
  }, []);

  // Move to next row: carry over twist & cheese, auto-increment crtnNo
  const moveToNextRow = useCallback((currentIndex: number) => {
    setRows(prev => {
      const next = [...prev];
      const currentRow = next[currentIndex];
      const nextIndex = currentIndex + 1;

      if (nextIndex >= next.length) {
        // Add new row
        const lastCrtn = parseInt(currentRow.crtnNo) || 0;
        const newRow = emptyCartonRow(String(lastCrtn + 1));
        // Carry over twist & cheese
        if (currentRow.twist) newRow.twist = currentRow.twist;
        if (currentRow.cheese) newRow.cheese = currentRow.cheese;
        return [...next, newRow];
      }

      // Carry over to existing next row
      const nextRow = next[nextIndex];
      if (!nextRow.twist && currentRow.twist) next[nextIndex] = { ...nextRow, twist: currentRow.twist };
      if (!next[nextIndex].cheese && currentRow.cheese) next[nextIndex] = { ...next[nextIndex], cheese: currentRow.cheese };
      return next;
    });

    // Focus the first visible field of the next row so user sees the auto-incremented value
    setTimeout(() => {
      const firstField = fieldOrder[0] || 'crtnNo';
      const el = inputRefs.current[`${currentIndex + 1}-${firstField}`];
      if (el) {
        el.focus();
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 80);
  }, [fieldOrder]);

  const handleRowKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, field: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const idx = fieldOrder.indexOf(field as any);
      if (idx >= 0 && idx < fieldOrder.length - 1) {
        // Move to next visible field in same row
        const nextField = fieldOrder[idx + 1];
        const el = inputRefs.current[`${rowIndex}-${nextField}`];
        if (el) el.focus();
      } else {
        // Last visible field (or auto-calc netWeight) — add a new row, like taka entry
        moveToNextRow(rowIndex);
      }
    } else if (e.key === 'ArrowDown') {
      // Move to the same field in the next row
      e.preventDefault();
      const el = inputRefs.current[`${rowIndex + 1}-${field}`];
      if (el) el.focus();
    } else if (e.key === 'ArrowUp') {
      // Move to the same field in the previous row
      e.preventDefault();
      if (rowIndex > 0) {
        const el = inputRefs.current[`${rowIndex - 1}-${field}`];
        if (el) el.focus();
      }
    }
  }, [fieldOrder, moveToNextRow]);

  const removeRow = (index: number) => {
    if (rows.length > 1) {
      setRows(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSave = () => {
    if (showCompanyColumn && !selectedTenantId) { toast.error('Please select a company'); return; }
    if (!headerData.yarnName.trim()) { toast.error('Item name is required'); return; }
    if (!isYarnNameAllowed(yarnNames, headerData.yarnName)) {
      toast.error(`Item name "${headerData.yarnName.trim()}" is not in the yarn-name master. Ask an admin to add it before submitting.`);
      return;
    }
    if (!headerData.buyerName.trim()) { toast.error('Buyer is required'); return; }
    if (!headerData.supplier.trim()) { toast.error('Supplier name is required'); return; }
    if (!headerData.supplierGst.trim()) { toast.error('Supplier GST number is required'); return; }
    if (!headerData.challanNo.trim()) { toast.error('Challan number is required'); return; }
    if (!isColourAllowed(colours, headerData.color, headerData.colorCode)) {
      toast.error(`Colour "${[headerData.color, headerData.colorCode].filter(Boolean).join(' / ')}" is not in the colour master. Ask an admin to add it before submitting.`);
      return;
    }

    const filledRows = rows.filter(r => !isRowEmpty(r));
    if (!filledRows.length) { toast.error('Add at least one carton row'); return; }

    // Per-row required fields: every non-empty row must have a Carton No, a net weight > 0, and a Location.
    // Iterate the full rows array so the row number matches the visible "Sr." column.
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (isRowEmpty(r)) continue;
      if (!r.crtnNo.trim()) { toast.error(`Row ${i + 1}: Carton No is required`); return; }
      if (!(Number(r.netWeight) > 0)) { toast.error(`Row ${i + 1}: Net weight must be greater than 0`); return; }
      // Location can't auto-fill, so only require it when the column is visible — otherwise a hidden
      // column would make submit impossible with no way to enter a value.
      if (isVisible('location') && !r.location.trim()) { toast.error(`Row ${i + 1}: Location is required`); return; }
    }

    createMutation.mutate({
      yarnName: headerData.yarnName,
      hsnCode: headerData.hsnCode,
      lot: headerData.lot,
      grade: headerData.grade,
      color: headerData.color,
      colorCode: headerData.colorCode,
      supplier: headerData.supplier,
      supplierGst: headerData.supplierGst,
      buyerName: headerData.buyerName,
      buyerClientId: headerData.buyerClientId,
      challanNo: headerData.challanNo,
      vehicleNo: headerData.vehicleNo,
      receivedDate: headerData.receivedDate,
      packagingType: headerData.packagingType,
      status: 'in_stock',
      tenantId: showCompanyColumn ? selectedTenantId : undefined,
      items: filledRows.map((r, i) => ({
        srNo: i + 1,
        crtnNo: r.crtnNo,
        twist: r.twist,
        cheese: Number(r.cheese) || 0,
        grossWeight: Number(r.grossWeight) || 0,
        tareWeight: Number(r.tareWeight) || 0,
        netWeight: Number(r.netWeight) || 0,
        location: r.location
      }))
    });
  };

  // Save header-only edits. Sends the lot fields (no carton `items`) so cartons stay as-is.
  const handleUpdate = () => {
    if (editingId === null) return;
    if (!headerData.yarnName.trim()) { toast.error('Item name is required'); return; }
    if (!isYarnNameAllowed(yarnNames, headerData.yarnName)) {
      toast.error(`Item name "${headerData.yarnName.trim()}" is not in the yarn-name master. Ask an admin to add it before submitting.`);
      return;
    }
    if (!headerData.buyerName.trim()) { toast.error('Buyer is required'); return; }
    if (headerData.supplier.trim() && !headerData.supplierGst.trim()) { toast.error('Supplier GST number is required when a supplier is entered'); return; }
    if (!isColourAllowed(colours, headerData.color, headerData.colorCode)) {
      toast.error(`Colour "${[headerData.color, headerData.colorCode].filter(Boolean).join(' / ')}" is not in the colour master. Ask an admin to add it before submitting.`);
      return;
    }
    updateMutation.mutate({
      id: editingId,
      data: {
        yarnName: headerData.yarnName,
        hsnCode: headerData.hsnCode,
        lot: headerData.lot,
        grade: headerData.grade,
        color: headerData.color,
        colorCode: headerData.colorCode,
        supplier: headerData.supplier,
        supplierGst: headerData.supplierGst,
        buyerName: headerData.buyerName,
        buyerClientId: headerData.buyerClientId,
        challanNo: headerData.challanNo,
        vehicleNo: headerData.vehicleNo,
        receivedDate: headerData.receivedDate,
        packagingType: headerData.packagingType,
      },
    });
  };

  const openActionMenu = (e: React.MouseEvent, id: number) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setActionMenuPos({ top: rect.bottom + 4, left: rect.right - 140 });
    setActionMenuId(id);
  };

  // Totals for add form
  const totalCrtn = rows.filter(r => !isRowEmpty(r)).length;
  const totalCheese = rows.reduce((s, r) => s + (Number(r.cheese) || 0), 0);
  const totalGrWt = rows.reduce((s, r) => s + (Number(r.grossWeight) || 0), 0);
  const totalTrWt = rows.reduce((s, r) => s + (Number(r.tareWeight) || 0), 0);
  const totalNetWt = rows.reduce((s, r) => s + (Number(r.netWeight) || 0), 0);

  const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '';
  const fmtNum = (n: number | null | undefined) => n != null ? parseFloat(String(n)).toFixed(2) : '';

  const statusBadge = (s: string) => {
    const map: Record<string, string> = {
      in_stock: 'bg-[#D7F7C2] border-[#A6EB84] text-[#006908]',
      partial: 'bg-[#FCEDB9] border-[#FCD579] text-[#A82C00]',
      consumed: 'bg-[#FFE0E0] border-[#FFB0B0] text-[#B91C1C]'
    };
    return map[s] || map.in_stock;
  };

  // Colour master modal — shared across list / add / edit views (portals to <body>).
  const colourModalNode = (colourModalOpen && isAdmin) ? createPortal(
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={() => setColourModalOpen(false)}>
      <div className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl border border-[#E1E6EF] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8EBF1]">
          <div>
            <h3 className="text-base font-bold text-[#1B202A]">Colour master</h3>
            <p className="text-xs text-[rgba(14,19,27,0.5)] mt-0.5">Only these colours can be used on Yarn Inward. Staff select; admins add here.</p>
          </div>
          <button onClick={() => setColourModalOpen(false)} className="p-1 rounded-lg text-[rgba(14,19,27,0.45)] hover:bg-[#F8F9FC]">✕</button>
        </div>
        <div className="flex items-end gap-2 px-5 py-3 border-b border-[#EEF0F3] bg-[#F8F9FC]">
          <div className="flex-1">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-[rgba(14,19,27,0.5)] mb-1">Colour name *</label>
            <input value={newColourName} onChange={(e) => setNewColourName(e.target.value)} placeholder="e.g. White"
              className="w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
          </div>
          <div className="w-32">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-[rgba(14,19,27,0.5)] mb-1">Code</label>
            <input value={newColourCode} onChange={(e) => setNewColourCode(e.target.value)} placeholder="WHT-01"
              className="w-full px-2.5 py-1.5 text-sm font-mono border border-[#E1E6EF] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
          </div>
          <button onClick={() => addColourMutation.mutate()} disabled={!newColourName.trim() || addColourMutation.isPending}
            className="shrink-0 px-4 py-1.5 text-sm font-semibold text-white bg-[#0055a9] rounded-lg hover:bg-[#00478f] disabled:opacity-40">Add</button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {colours.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-[rgba(14,19,27,0.5)]">No colours yet. Add the colours your challans use — until then, any colour is accepted.</div>
          ) : colours.map((c: any) => (
            <div key={c.id} className="flex items-center gap-3 px-5 py-2.5 border-b border-[#F4F5F8]">
              <span className="flex-1 text-sm font-semibold text-[#0E131B]">{c.name}</span>
              <span className="text-xs font-mono text-[rgba(14,19,27,0.6)]">{c.code || '—'}</span>
              <button onClick={() => removeColourMutation.mutate(c.id)} disabled={removeColourMutation.isPending}
                className="shrink-0 h-8 w-8 rounded-lg border border-red-100 text-red-500 flex items-center justify-center hover:bg-red-50 disabled:opacity-50" title="Remove">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // Yarn/item-name master modal — shared across list / add / edit views.
  const yarnNameModalNode = (yarnNameModalOpen && isAdmin) ? createPortal(
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={() => setYarnNameModalOpen(false)}>
      <div className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl border border-[#E1E6EF] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8EBF1]">
          <div>
            <h3 className="text-base font-bold text-[#1B202A]">Yarn name master</h3>
            <p className="text-xs text-[rgba(14,19,27,0.5)] mt-0.5">Only these item names can be used on Yarn Inward. Staff select; admins add here.</p>
          </div>
          <button onClick={() => setYarnNameModalOpen(false)} className="p-1 rounded-lg text-[rgba(14,19,27,0.45)] hover:bg-[#F8F9FC]">✕</button>
        </div>
        <div className="flex items-end gap-2 px-5 py-3 border-b border-[#EEF0F3] bg-[#F8F9FC]">
          <div className="flex-1">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-[rgba(14,19,27,0.5)] mb-1">Item name *</label>
            <input value={newYarnMasterName} onChange={(e) => setNewYarnMasterName(e.target.value)} placeholder="e.g. 150/216 SD SIM YARN"
              className="w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
          </div>
          <button onClick={() => addYarnNameMutation.mutate()} disabled={!newYarnMasterName.trim() || addYarnNameMutation.isPending}
            className="shrink-0 px-4 py-1.5 text-sm font-semibold text-white bg-[#0055a9] rounded-lg hover:bg-[#00478f] disabled:opacity-40">Add</button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {yarnNames.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-[rgba(14,19,27,0.5)]">No yarn names yet. Add the item names you use — until then, any item name is accepted.</div>
          ) : yarnNames.map((y: any) => (
            <div key={y.id} className="flex items-center gap-3 px-5 py-2.5 border-b border-[#F4F5F8]">
              <span className="flex-1 text-sm font-semibold text-[#0E131B]">{y.name}</span>
              <button onClick={() => removeYarnNameMutation.mutate(y.id)} disabled={removeYarnNameMutation.isPending}
                className="shrink-0 h-8 w-8 rounded-lg border border-red-100 text-red-500 flex items-center justify-center hover:bg-red-50 disabled:opacity-50" title="Remove">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // COLOR / COLOR CODE render as searchable dropdowns once colours are curated (staff pick from the
  // master); while the master is empty they stay free-text (nothing is enforced yet). Picking a row
  // commits that exact Colour object — not a name/code string re-matched afterwards — so two colours
  // that share a name but differ only by code (common for dye lots) can't resolve to the wrong one.
  const renderColorField = (cls: string) => (colours.length > 0 ? (
    <ColourDropdown colours={colours as Colour[]} display={headerData.color}
      isSelected={(c) => c.name === headerData.color && (c.code || '') === (headerData.colorCode || '')}
      onPick={(c) => setHeaderData(p => ({ ...p, color: c.name, colorCode: c.code || '' }))}
      placeholder="— Select colour —" className={cls} />
  ) : (
    <input type="text" value={headerData.color} onChange={e => setHeaderData(p => ({ ...p, color: e.target.value }))} className={cls} placeholder="e.g. White" />
  ));
  const renderCodeField = (cls: string) => (colours.length > 0 ? (
    <ColourDropdown colours={(colours as Colour[]).filter((c) => c.code)} display={headerData.colorCode}
      isSelected={(c) => c.code === headerData.colorCode}
      onPick={(c) => setHeaderData(p => ({ ...p, colorCode: c.code || '' }))}
      placeholder="— Select code —" className={cls} />
  ) : (
    <input type="text" value={headerData.colorCode} onChange={e => setHeaderData(p => ({ ...p, colorCode: e.target.value }))} className={cls} placeholder="e.g. WHT-01" />
  ));

  // ─── EDIT MODE (full detail page: header fields editable, cartons shown read-only for context) ───
  if (mode === 'edit') {
    const editEntry = entries.find((e: any) => e.id === editingId);
    const editItems = ((editEntry?.items as any[]) || []).slice().sort((a: any, b: any) => a.srNo - b.srNo);
    const closeEdit = () => { setMode('list'); setEditingId(null); };
    const inputCls = "w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]";
    const labelCls = "block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1";
    return (
      <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="px-2 py-3 space-y-3">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={closeEdit} title="Back"
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl border border-[#E1E6EF] bg-white text-gray-600 hover:bg-[#F8F9FC] transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-[#1B202A] truncate">Edit Yarn Entry</h1>
            <p className="text-xs text-[rgba(14,19,27,0.5)] truncate">
              {headerData.yarnName || '—'}{headerData.lot ? ` · Lot ${headerData.lot}` : ''} · {editItems.length} carton{editItems.length === 1 ? '' : 's'}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <button onClick={closeEdit}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] transition-colors">Cancel</button>
            <button onClick={handleUpdate} disabled={updateMutation.isPending}
              className="px-5 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780] disabled:opacity-50 transition-colors">
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Details — editable header fields */}
        <div className="bg-white rounded-xl border border-[#E8EBF1] p-4">
          <h2 className="text-xs font-bold uppercase tracking-wide text-[rgba(14,19,27,0.4)] mb-3">Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>BUYER *</label>
              <ClientDropdown value={headerData.buyerName} clients={clients} allowCreate={false}
                placeholder="Select buyer party…"
                onChange={(name, clientId) => setHeaderData(p => ({ ...p, buyerName: name, buyerClientId: clientId || null }))} />
            </div>
            <div>
              <label className="flex items-center justify-between text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">
                <span>ITEM NAME *</span>
                {isAdmin && <button type="button" onClick={() => setYarnNameModalOpen(true)} className="text-[10px] font-semibold text-[#0055a9] hover:underline normal-case">Manage</button>}
              </label>
              <input type="text" list="yarn-names" value={headerData.yarnName} onChange={e => setHeaderData(p => ({ ...p, yarnName: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-sm font-bold border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="e.g. 150/216 SD SIM YARN" />
              <datalist id="yarn-names">{yarnNames.map((y: any) => <option key={y.id} value={y.name} />)}</datalist>
            </div>
            {isVisible('challanNo') && (
              <div><label className={labelCls}>CHALLAN NO *</label>
                <input type="text" value={headerData.challanNo} onChange={e => setHeaderData(p => ({ ...p, challanNo: e.target.value }))} className={inputCls} placeholder="e.g. 44469" /></div>
            )}
            {isVisible('receivedDate') && (
              <div><label className={labelCls}>DATE</label>
                <input type="date" value={headerData.receivedDate} onChange={e => setHeaderData(p => ({ ...p, receivedDate: e.target.value }))} className={inputCls} /></div>
            )}
            {isVisible('vehicleNo') && (
              <div><label className={labelCls}>VEHICLE NO</label>
                <input type="text" value={headerData.vehicleNo} onChange={e => setHeaderData(p => ({ ...p, vehicleNo: e.target.value }))} className={inputCls} placeholder="e.g. GJ19Z9792" /></div>
            )}
            {isVisible('hsnCode') && (
              <div><label className={labelCls}>HSN CODE</label>
                <input type="text" value={headerData.hsnCode} onChange={e => setHeaderData(p => ({ ...p, hsnCode: e.target.value }))} className={inputCls} placeholder="e.g. 54023300" /></div>
            )}
            {isVisible('color') && (
              <div>
                <label className="flex items-center justify-between text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">
                  <span>COLOR</span>
                  {isAdmin && <button type="button" onClick={() => setColourModalOpen(true)} className="text-[10px] font-semibold text-[#0055a9] hover:underline normal-case">Manage</button>}
                </label>
                {renderColorField(inputCls)}
              </div>
            )}
            {isVisible('colorCode') && (
              <div><label className={labelCls}>COLOR CODE</label>
                {renderCodeField(inputCls)}
              </div>
            )}
            {isVisible('supplier') && (
              <div><label className={labelCls}>SUPPLIER / MILL *</label>
                <input type="text" value={headerData.supplier} onChange={e => setHeaderData(p => ({ ...p, supplier: e.target.value }))} className={inputCls} placeholder="e.g. NJ Textile" /></div>
            )}
            {isVisible('supplier') && (
              <div><label className={labelCls}>SUPPLIER GST *</label>
                <div className="flex gap-2">
                  <input type="text" value={headerData.supplierGst}
                    onChange={e => { setHeaderData(p => ({ ...p, supplierGst: e.target.value.toUpperCase() })); setSupplierGstFromScan(false); }}
                    className={inputCls} placeholder="e.g. 24ABCDE1234F1Z5" maxLength={15} />
                  {!supplierGstFromScan && (
                    <button type="button" onClick={handleFetchSupplierGst} disabled={gstLoading || !isValidGstin(headerData.supplierGst)}
                      className="shrink-0 px-3 py-1.5 rounded-xl bg-[#0055a9] text-white text-xs font-medium hover:bg-[#00478f] disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isValidGstin(headerData.supplierGst) ? 'Auto-fill supplier name from this GST number' : 'Enter a valid 15-character GSTIN to enable'}>{gstLoading ? '…' : 'Fetch'}</button>
                  )}
                </div></div>
            )}
            {isVisible('lot') && (
              <div><label className={labelCls}>LOT</label>
                <input type="text" value={headerData.lot} onChange={e => setHeaderData(p => ({ ...p, lot: e.target.value }))} className={inputCls} placeholder="114" /></div>
            )}
            {isVisible('grade') && (
              <div><label className={labelCls}>GRADE</label>
                <input type="text" value={headerData.grade} onChange={e => setHeaderData(p => ({ ...p, grade: e.target.value }))} className={inputCls} placeholder="1ST" /></div>
            )}
          </div>
        </div>

        {/* Cartons — read-only, for context ("get idea" of what's in this lot) */}
        <div className="bg-white rounded-xl border border-[#E8EBF1] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8EBF1] gap-2 flex-wrap">
            <h2 className="text-xs font-bold uppercase tracking-wide text-[rgba(14,19,27,0.4)]">Cartons <span className="text-[rgba(14,19,27,0.3)]">({editItems.length})</span></h2>
            <span className="text-[11px] text-[rgba(14,19,27,0.4)]">Read-only — weights &amp; labels aren't changed by an edit</span>
          </div>
          {editItems.length > 0 ? (
            <div className="overflow-x-auto max-h-[46vh] overflow-y-auto">
              <table className="min-w-full">
                <thead className="sticky top-0">
                  <tr className="bg-[#F0F2F8]">
                    <th className="px-4 py-1.5 text-center text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">Sr.</th>
                    <th className="px-4 py-1.5 text-center text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">CRTNNO</th>
                    <th className="px-4 py-1.5 text-center text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">Twist</th>
                    <th className="px-4 py-1.5 text-center text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">Chs.</th>
                    <th className="px-4 py-1.5 text-right text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">GrWt</th>
                    <th className="px-4 py-1.5 text-right text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">TrWt</th>
                    <th className="px-4 py-1.5 text-right text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">NetWt</th>
                    <th className="px-4 py-1.5 text-center text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {editItems.map((item: any) => (
                    <tr key={item.id} className="border-t border-[#E8EBF1]/60">
                      <td className="px-4 py-1.5 text-center text-xs text-[rgba(14,19,27,0.6)]">{item.srNo}</td>
                      <td className="px-4 py-1.5 text-center text-xs font-medium text-[#0E131B]">{item.crtnNo}</td>
                      <td className="px-4 py-1.5 text-center text-xs text-[#0E131B]">{item.twist || '-'}</td>
                      <td className="px-4 py-1.5 text-center text-xs text-[#0E131B]">{item.cheese || '-'}</td>
                      <td className="px-4 py-1.5 text-right text-xs tabular-nums text-[#0E131B]">{fmtNum(item.grossWeight)}</td>
                      <td className="px-4 py-1.5 text-right text-xs tabular-nums text-[#0E131B]">{fmtNum(item.tareWeight)}</td>
                      <td className="px-4 py-1.5 text-right text-xs font-semibold tabular-nums text-emerald-700">{fmtNum(item.netWeight)}</td>
                      <td className="px-4 py-1.5 text-center text-xs text-[#0E131B]">{item.location || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-4 py-6 text-center text-sm text-[rgba(14,19,27,0.4)]">No cartons on this entry.</p>
          )}
        </div>
        {colourModalNode}
        {yarnNameModalNode}
      </motion.div>
    );
  }

  // ─── ADD MODE ───
  if (mode === 'add') {
    return (
      <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="px-2 py-3 space-y-3">
        {/* Header Card: Buyer, Challan, Date, Vehicle, Item info */}
        <div className="bg-white rounded-xl border border-[#E8EBF1] p-4 space-y-4">
          {showCompanyColumn && (
            <div className="max-w-xs">
              <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">COMPANY *</label>
              <CompanySelect value={selectedTenantId} onChange={setSelectedTenantId} />
            </div>
          )}
          {/* Row 1: Buyer, Challan No, Date, Vehicle */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">BUYER *</label>
              <ClientDropdown
                value={headerData.buyerName}
                clients={clients}
                allowCreate={false}
                placeholder="Select buyer party…"
                onChange={(name, clientId) => setHeaderData(p => ({ ...p, buyerName: name, buyerClientId: clientId || null }))}
              />
            </div>
            {isVisible('challanNo') && (
            <div>
              <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">CHALLAN NO *</label>
              <input type="text" value={headerData.challanNo} onChange={e => setHeaderData(p => ({ ...p, challanNo: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-sm font-semibold border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="e.g. 44469" />
            </div>
            )}
            {isVisible('receivedDate') && (
            <div>
              <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">DATE</label>
              <input type="date" value={headerData.receivedDate} onChange={e => setHeaderData(p => ({ ...p, receivedDate: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
            </div>
            )}
            {isVisible('vehicleNo') && (
            <div>
              <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">VEHICLE NO</label>
              <input type="text" value={headerData.vehicleNo} onChange={e => setHeaderData(p => ({ ...p, vehicleNo: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="e.g. GJ19Z9792" />
            </div>
            )}
          </div>

          {/* Row 2: Item Name, HSN, Color, Supplier */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="flex items-center justify-between text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">
                <span>ITEM NAME *</span>
                {isAdmin && <button type="button" onClick={() => setYarnNameModalOpen(true)} className="text-[10px] font-semibold text-[#0055a9] hover:underline normal-case">Manage</button>}
              </label>
              <input type="text" list="yarn-names" value={headerData.yarnName} onChange={e => setHeaderData(p => ({ ...p, yarnName: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-sm font-bold border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="e.g. 150/216 SD SIM YARN" />
              <datalist id="yarn-names">{yarnNames.map((y: any) => <option key={y.id} value={y.name} />)}</datalist>
            </div>
            {isVisible('hsnCode') && (
            <div>
              <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">HSN CODE</label>
              <input type="text" value={headerData.hsnCode} onChange={e => setHeaderData(p => ({ ...p, hsnCode: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="e.g. 54023300" />
            </div>
            )}
            {isVisible('color') && (
            <div>
              <label className="flex items-center justify-between text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">
                <span>COLOR</span>
                {isAdmin && <button type="button" onClick={() => setColourModalOpen(true)} className="text-[10px] font-semibold text-[#0055a9] hover:underline normal-case">Manage</button>}
              </label>
              {renderColorField("w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]")}
            </div>
            )}
            {isVisible('colorCode') && (
            <div>
              <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">COLOR CODE</label>
              {renderCodeField("w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]")}
            </div>
            )}
            {isVisible('supplier') && (
            <div>
              <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">SUPPLIER / MILL *</label>
              <input type="text" value={headerData.supplier} onChange={e => setHeaderData(p => ({ ...p, supplier: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="e.g. NJ Textile" />
            </div>
            )}
            {isVisible('supplier') && (
            <div>
              <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">SUPPLIER GST *</label>
              <div className="flex gap-2">
                <input type="text" value={headerData.supplierGst} onChange={e => { setHeaderData(p => ({ ...p, supplierGst: e.target.value.toUpperCase() })); setSupplierGstFromScan(false); }} maxLength={15}
                  className="w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="e.g. 24ABCDE1234F1Z5" />
                {!supplierGstFromScan && (
                  <button type="button" onClick={handleFetchSupplierGst} disabled={gstLoading || !isValidGstin(headerData.supplierGst)}
                    className="shrink-0 px-3 py-1.5 rounded-xl bg-[#0055a9] text-white text-xs font-medium hover:bg-[#00478f] disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isValidGstin(headerData.supplierGst) ? 'Auto-fill supplier name from this GST number' : 'Enter a valid 15-character GSTIN to enable'}>{gstLoading ? '…' : 'Fetch'}</button>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Row 3: Lot, Grade + Stats + Actions */}
          <div className="flex items-end gap-4 flex-wrap">
            {isVisible('lot') && (
            <div>
              <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">LOT</label>
              <input type="text" value={headerData.lot} onChange={e => setHeaderData(p => ({ ...p, lot: e.target.value }))}
                className="w-20 px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="114" />
            </div>
            )}
            {isVisible('grade') && (
            <div>
              <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1">GRADE</label>
              <input type="text" value={headerData.grade} onChange={e => setHeaderData(p => ({ ...p, grade: e.target.value }))}
                className="w-20 px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="1ST" />
            </div>
            )}

            {/* Summary stats */}
            <div className="flex items-center gap-3 ml-auto flex-wrap">
              <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2">
                <div className="w-7 h-7 rounded-md bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 text-xs font-black">C</span>
                </div>
                <div>
                  <p className="text-[10px] text-blue-400 font-semibold uppercase leading-none">Crtn</p>
                  <input type="number" value={manualCrtn !== null ? manualCrtn : totalCrtn}
                    onChange={e => setManualCrtn(e.target.value)}
                    onBlur={() => { if (manualCrtn === '' || manualCrtn === String(totalCrtn)) setManualCrtn(null); }}
                    className="w-14 text-base font-bold text-blue-700 leading-tight tabular-nums bg-transparent border-none outline-none p-0 focus:ring-0" />
                </div>
              </div>
              <div className="flex items-center gap-2 bg-emerald-50 rounded-lg px-3 py-2">
                <div className="w-7 h-7 rounded-md bg-emerald-100 flex items-center justify-center">
                  <span className="text-emerald-600 text-xs font-black">N</span>
                </div>
                <div>
                  <p className="text-[10px] text-emerald-400 font-semibold uppercase leading-none">Net Wt</p>
                  <input type="number" step="0.01" value={manualNetWt !== null ? manualNetWt : totalNetWt.toFixed(2)}
                    onChange={e => setManualNetWt(e.target.value)}
                    onBlur={() => { if (manualNetWt === '' || manualNetWt === totalNetWt.toFixed(2)) setManualNetWt(null); }}
                    className="w-16 text-base font-bold text-emerald-700 leading-tight tabular-nums bg-transparent border-none outline-none p-0 focus:ring-0" />
                </div>
              </div>

              {/* Scan Challan Button */}
              <input type="file" ref={fileInputRef} accept="image/*" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleScanChallan(f); }} />
              <button onClick={() => fileInputRef.current?.click()} disabled={isScanning}
                className="inline-flex items-center gap-1.5 px-3 py-[7px] text-sm font-semibold text-[#0055a9] bg-white border border-[#0055a9] rounded-xl hover:bg-[#F8F9FC] disabled:opacity-50 transition-colors">
                {isScanning ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-[#0055a9] border-t-transparent"></div>
                    Scanning...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    Scan Challan
                  </>
                )}
              </button>

              <button onClick={() => setShowFieldSettings(true)} title="Choose which fields are visible"
                className="inline-flex items-center gap-1.5 px-3 py-[7px] text-sm font-medium text-gray-600 bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] shadow-[1px_1px_3px_rgba(0,0,0,0.06)] transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                Fields
              </button>
              <button onClick={() => setMode('list')}
                className="px-3 py-[7px] text-sm font-medium text-gray-600 bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] shadow-[1px_1px_3px_rgba(0,0,0,0.06)] transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={createMutation.isPending}
                className="px-4 py-[7px] text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780] disabled:opacity-50 transition-colors">
                {createMutation.isPending ? 'Saving...' : 'Save All'}
              </button>
            </div>
          </div>
        </div>

        {/* Carton Rows Table */}
        <div className="bg-white rounded-xl border border-[#E8EBF1] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-y border-[#F1F3F9]">
                  <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider w-12">Sr.</th>
                  {isVisible('crtnNo') && <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider">CRTNNO</th>}
                  {isVisible('twist') && <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider w-16">Twist</th>}
                  {isVisible('cheese') && <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider w-16">Chs.</th>}
                  {isVisible('grossWeight') && <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider">GrWt</th>}
                  {isVisible('tareWeight') && <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider">TrWt</th>}
                  {isVisible('netWeight') && <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider">NetWt</th>}
                  {isVisible('location') && <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider">Location</th>}
                  <th className="px-1 py-2 w-8 bg-[#F8F9FC]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => {
                  const filled = !isRowEmpty(row);
                  return (
                    <tr key={i} className={filled ? 'bg-blue-50/30' : ''}>
                      <td className="px-2 py-1 text-center text-sm font-semibold text-[#0055a9] border-r border-gray-100">{i + 1}</td>
                      {isVisible('crtnNo') && (
                      <td className="px-1 py-1 border-r border-gray-100">
                        <input type="text" value={row.crtnNo} onChange={e => updateRow(i, 'crtnNo', e.target.value)}
                          ref={el => setInputRef(i, 'crtnNo', el)} onKeyDown={e => handleRowKeyDown(e, i, 'crtnNo')}
                          className="w-full px-2 py-1 text-sm text-center font-semibold border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent tabular-nums" />
                      </td>
                      )}
                      {isVisible('twist') && (
                      <td className="px-1 py-1 border-r border-gray-100">
                        <input type="text" value={row.twist} onChange={e => updateRow(i, 'twist', e.target.value)}
                          ref={el => setInputRef(i, 'twist', el)} onKeyDown={e => handleRowKeyDown(e, i, 'twist')}
                          className="w-full px-2 py-1 text-sm text-center border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent" placeholder="S" />
                      </td>
                      )}
                      {isVisible('cheese') && (
                      <td className="px-1 py-1 border-r border-gray-100">
                        <input type="number" value={row.cheese} onChange={e => updateRow(i, 'cheese', e.target.value)}
                          ref={el => setInputRef(i, 'cheese', el)} onKeyDown={e => handleRowKeyDown(e, i, 'cheese')}
                          className="w-full px-2 py-1 text-sm text-center border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent tabular-nums" placeholder="6" />
                      </td>
                      )}
                      {isVisible('grossWeight') && (
                      <td className="px-1 py-1 border-r border-gray-100">
                        <input type="number" step="0.01" value={row.grossWeight} onChange={e => updateRow(i, 'grossWeight', e.target.value)}
                          ref={el => setInputRef(i, 'grossWeight', el)} onKeyDown={e => handleRowKeyDown(e, i, 'grossWeight')}
                          className="w-full px-2 py-1 text-sm text-right border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent tabular-nums" placeholder="0.00" />
                      </td>
                      )}
                      {isVisible('tareWeight') && (
                      <td className="px-1 py-1 border-r border-gray-100">
                        <input type="number" step="0.01" value={row.tareWeight} onChange={e => updateRow(i, 'tareWeight', e.target.value)}
                          ref={el => setInputRef(i, 'tareWeight', el)} onKeyDown={e => handleRowKeyDown(e, i, 'tareWeight')}
                          className="w-full px-2 py-1 text-sm text-right border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent tabular-nums" placeholder="0.00" />
                      </td>
                      )}
                      {isVisible('netWeight') && (
                      <td className="px-1 py-1 border-r border-gray-100">
                        <input type="number" step="0.01" value={row.netWeight} onChange={e => updateRow(i, 'netWeight', e.target.value)}
                          ref={el => setInputRef(i, 'netWeight', el)} onKeyDown={e => handleRowKeyDown(e, i, 'netWeight')}
                          className="w-full px-2 py-1 text-sm text-right font-semibold text-emerald-700 border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent tabular-nums" placeholder="0.00" />
                      </td>
                      )}
                      {isVisible('location') && (
                      <td className="px-1 py-1 border-r border-gray-100">
                        <LocationDropdown value={row.location} onChange={v => updateRow(i, 'location', v)} options={locationOptions} onEnter={() => moveToNextRow(i)} />
                      </td>
                      )}
                      <td className="px-1 py-1 text-center">
                        {filled && (
                          <button type="button" onClick={() => removeRow(i)} className="text-[rgba(14,19,27,0.4)] hover:text-[#E02D3C] hover:bg-red-50 p-0.5 rounded" tabIndex={-1}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-y border-[#F1F3F9] bg-[#F8F9FC]">
                  <td className="px-3 py-2 text-xs font-bold text-[rgba(14,19,27,0.65)]" colSpan={1 + (isVisible('crtnNo') ? 1 : 0)}>TOTAL: {totalCrtn} Crtn</td>
                  {isVisible('twist') && <td className="px-3 py-2"></td>}
                  {isVisible('cheese') && <td className="px-3 py-2 text-sm font-bold text-center tabular-nums text-[#0E131B]">{totalCheese}</td>}
                  {isVisible('grossWeight') && <td className="px-3 py-2 text-sm font-bold text-right tabular-nums text-blue-700">{totalGrWt.toFixed(2)}</td>}
                  {isVisible('tareWeight') && <td className="px-3 py-2 text-sm font-bold text-right tabular-nums text-orange-600">{totalTrWt.toFixed(2)}</td>}
                  {isVisible('netWeight') && <td className="px-3 py-2 text-sm font-bold text-right tabular-nums text-emerald-700">{totalNetWt.toFixed(2)}</td>}
                  {isVisible('location') && <td className="px-3 py-2"></td>}
                  <td className="px-1 py-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {showFieldSettings && createPortal(
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowFieldSettings(false)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8EBF1]">
                <div>
                  <h3 className="text-lg font-bold text-[#1B202A]">Field Settings</h3>
                  <p className="text-xs text-[rgba(14,19,27,0.5)]">Choose which fields show on this form. Saved to your account.</p>
                </div>
                <button onClick={() => setShowFieldSettings(false)} className="p-1.5 rounded-lg hover:bg-[#F8F9FC] text-gray-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="px-5 py-4 overflow-y-auto space-y-5">
                <div>
                  <p className="text-xs font-semibold text-[rgba(14,19,27,0.5)] uppercase tracking-wider mb-2">Header Fields</p>
                  <div className="grid grid-cols-2 gap-2">
                    {YM_HEADER_FIELDS.map(f => (
                      <label key={f.key} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${f.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${isVisible(f.key) ? 'border-[#0055a9] bg-[#0055a9]/5' : 'border-[#E1E6EF] bg-white'}`}>
                        <input type="checkbox" checked={isVisible(f.key)} disabled={f.required} onChange={() => toggleField(f.key)}
                          className="rounded border-gray-300 text-[#0055a9] focus:ring-[#0055a9]" />
                        <span className="text-[#0E131B]">{f.label}{f.required && <span className="text-[10px] text-gray-400 ml-1">(required)</span>}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[rgba(14,19,27,0.5)] uppercase tracking-wider mb-2">Carton Table Columns</p>
                  <div className="grid grid-cols-2 gap-2">
                    {YM_TABLE_COLUMNS.map(f => (
                      <label key={f.key} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer ${isVisible(f.key) ? 'border-[#0055a9] bg-[#0055a9]/5' : 'border-[#E1E6EF] bg-white'}`}>
                        <input type="checkbox" checked={isVisible(f.key)} onChange={() => toggleField(f.key)}
                          className="rounded border-gray-300 text-[#0055a9] focus:ring-[#0055a9]" />
                        <span className="text-[#0E131B]">{f.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-t border-[#E8EBF1] bg-[#F8F9FC]">
                <button onClick={resetFields} className="text-sm font-medium text-gray-600 hover:text-[#0055a9]">Show all</button>
                <button onClick={() => setShowFieldSettings(false)} className="px-4 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780]">Done</button>
              </div>
            </div>
          </div>,
          document.body
        )}
        {colourModalNode}
        {yarnNameModalNode}
      </motion.div>
    );
  }

  // ─── LIST MODE ───
  return (
    <>
      <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="px-2 py-4">
        <div className="bg-white rounded-xl border border-[#E8EBF1]">
          {/* Header bar */}
          <div className="flex items-center px-5 py-4 border-b border-[#E8EBF1] gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-[#1B202A]">Yarn Inward</h1>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <div className="relative">
                <svg className="w-4 h-4 text-[rgba(14,19,27,0.4)] absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input type="text" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} placeholder="Search yarn..."
                  className="pl-9 pr-3 py-2 text-sm bg-[#F8F9FC] border border-[#E1E6EF] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] w-full sm:w-48" />
              </div>
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                className="px-4 py-2 text-sm bg-white border border-[#E1E6EF] rounded-xl shadow-[1px_1px_3px_rgba(0,0,0,0.06)] focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]">
                <option value="">All Status</option>
                <option value="in_stock">In Stock</option>
                <option value="partial">Partial</option>
                <option value="consumed">Consumed</option>
              </select>

              {/* Received-date filter */}
              <input type="date" value={filterDate} onChange={e => { setFilterDate(e.target.value); setPage(1); }}
                title="Filter by received date"
                className="px-3 sm:px-4 py-2 text-sm bg-white border border-[#E1E6EF] rounded-xl shadow-[1px_1px_3px_rgba(0,0,0,0.06)] focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />

              {(filterDate || statusFilter || searchQuery) && (
                <button onClick={() => { setFilterDate(''); setStatusFilter(''); setSearchQuery(''); setPage(1); }}
                  className="text-xs text-[#0055a9] hover:text-[#0d2780] font-medium px-1">
                  Clear
                </button>
              )}
              <button onClick={() => setShowListSettings(true)} title="Choose which columns to show"
                className="inline-flex items-center gap-1.5 text-gray-600 bg-white border border-[#E1E6EF] rounded-xl px-3 py-2 text-sm font-medium hover:bg-[#F8F9FC] transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                Columns
              </button>
              <button onClick={() => { openAdd(); setTimeout(() => fileInputRef.current?.click(), 100); }}
                disabled={isScanning || !canCreate}
                title={!canCreate ? 'You have read-only access' : undefined}
                className="inline-flex items-center gap-1.5 text-[#0055a9] bg-white border border-[#0055a9] rounded-xl px-4 py-2 text-sm font-semibold hover:bg-[#F8F9FC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                Scan Challan
              </button>
              <button onClick={openAdd}
                disabled={!canCreate}
                title={!canCreate ? 'You have read-only access' : undefined}
                className="inline-flex items-center gap-1.5 bg-[#0055a9] text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-[#0B2278] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                New Entry
              </button>
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0055a9] border-t-transparent mx-auto mb-3"></div></div>
          ) : entries.length > 0 ? (
            <>
              {/* Mobile: stacked cards (the desktop table below is hidden on phones) */}
              <div className="sm:hidden divide-y divide-[#F1F3F9]">
                {entries.map((entry: any) => (
                  <div key={entry.id} className="px-4 py-3 active:bg-[#FAFBFD] transition-colors"
                    onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#0E131B] truncate">
                          {entry.yarnName}{entry.color ? <span className="text-[rgba(14,19,27,0.5)] font-normal"> · {entry.color}</span> : null}
                        </p>
                        <p className="text-xs text-[rgba(14,19,27,0.5)] truncate mt-0.5">
                          {entry.buyerName || '—'}{entry.lot ? ` · Lot ${entry.lot}` : ''} · {fmtDate(entry.receivedDate)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusBadge(entry.status)}`}>
                          {entry.status === 'in_stock' ? 'In Stock' : entry.status === 'partial' ? 'Partial' : 'Consumed'}
                        </span>
                        <button onClick={(e) => { e.stopPropagation(); openActionMenu(e, entry.id); }} aria-label="Actions"
                          className="p-2.5 -mr-1.5 rounded-lg text-[rgba(14,19,27,0.4)] active:bg-[#F0F2F8]">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><circle cx="10" cy="4" r="1.5" /><circle cx="10" cy="10" r="1.5" /><circle cx="10" cy="16" r="1.5" /></svg>
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <div className="rounded-lg bg-[#F8F9FC] px-2 py-1.5 text-center">
                        <p className="text-[10px] text-[rgba(14,19,27,0.4)] uppercase font-semibold">Crtn</p>
                        <p className="text-sm font-bold text-[#0E131B] tabular-nums">{entry.totalCartons || '—'}</p>
                      </div>
                      <div className="rounded-lg bg-[#F8F9FC] px-2 py-1.5 text-center">
                        <p className="text-[10px] text-[rgba(14,19,27,0.4)] uppercase font-semibold">Net Wt</p>
                        <p className="text-sm font-bold text-emerald-700 tabular-nums">{entry.totalNetWeight ? fmtNum(entry.totalNetWeight) : '—'}</p>
                      </div>
                      <div className="rounded-lg bg-[#F8F9FC] px-2 py-1.5 text-center min-w-0">
                        <p className="text-[10px] text-[rgba(14,19,27,0.4)] uppercase font-semibold">Bin</p>
                        <p className="text-sm font-bold text-[#0E131B] truncate">{lotLocations(entry) || '—'}</p>
                      </div>
                    </div>
                    {expandedId === entry.id && entry.items?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
                        {entry.items.slice().sort((a: any, b: any) => a.srNo - b.srNo).map((item: any) => (
                          <span key={item.id} className="text-[11px] font-medium px-2 py-1 rounded-md bg-[#0055a9]/5 text-[#0055a9] border border-[#0055a9]/15 tabular-nums">
                            {item.crtnNo} · {fmtNum(item.netWeight)} · {item.location || '—'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="z-10 shadow-[0_1px_0_0_#E8EBF1]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Item</th>
                    {isListVisible('buyer') && <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Buyer</th>}
                    {isListVisible('supplier') && <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Supplier</th>}
                    {isListVisible('lot') && <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Lot</th>}
                    {isListVisible('crtn') && <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Crtn</th>}
                    {isListVisible('crtnNo') && <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Crtn No</th>}
                    {isListVisible('cheese') && <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Cheese</th>}
                    {isListVisible('grossWeight') && <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Gr.Wt</th>}
                    {isListVisible('tareWeight') && <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Tr.Wt</th>}
                    {isListVisible('netWeight') && <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Net Wt</th>}
                    {isListVisible('location') && <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Location</th>}
                    {isListVisible('date') && <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Date</th>}
                    {isListVisible('status') && <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Status</th>}
                    {showCompanyColumn && <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Company</th>}
                    <th className="px-4 py-3 w-14 bg-[#F8F9FC]"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry: any, i: number) => (
                    <React.Fragment key={entry.id}>
                      <motion.tr initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                        className="border-b border-[#F1F3F9] hover:bg-[#FAFBFD] transition-colors cursor-pointer"
                        onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}>
                        <td className="px-4 py-4 text-sm">
                          <div>
                            <span className="font-semibold text-[#0E131B]">{entry.yarnName}</span>
                            {entry.color && <span className="ml-1.5 text-[rgba(14,19,27,0.5)]">- {entry.color}</span>}
                            {entry.hsnCode && <div className="text-[11px] text-[rgba(14,19,27,0.4)] mt-0.5">HSN: {entry.hsnCode}</div>}
                          </div>
                        </td>
                        {isListVisible('buyer') && <td className="px-4 py-4 text-sm text-[#0E131B]">{entry.buyerName || <span className="text-[rgba(14,19,27,0.3)]">-</span>}</td>}
                        {isListVisible('supplier') && <td className="px-4 py-4 text-sm text-[#0E131B]">{entry.supplier || <span className="text-[rgba(14,19,27,0.3)]">-</span>}</td>}
                        {isListVisible('lot') && <td className="px-4 py-4 text-center text-sm text-[#0E131B]">{entry.lot || <span className="text-[rgba(14,19,27,0.3)]">-</span>}</td>}
                        {isListVisible('crtn') && <td className="px-4 py-4 text-center text-sm text-[#0E131B] tabular-nums">{entry.totalCartons || <span className="text-[rgba(14,19,27,0.3)]">-</span>}</td>}
                        {isListVisible('crtnNo') && (
                        <td className="px-4 py-4 text-left text-sm text-[#0E131B]">
                          {/* max-w lives on the inner block: an auto-layout table ignores max-width on the cell itself */}
                          <div className="max-w-[220px] truncate tabular-nums" title={lotCrtnNos(entry)}>
                            {lotCrtnNos(entry) || <span className="text-[rgba(14,19,27,0.3)]">-</span>}
                          </div>
                        </td>
                        )}
                        {isListVisible('cheese') && <td className="px-4 py-4 text-center text-sm text-[#0E131B] tabular-nums">{entry.totalCheese || <span className="text-[rgba(14,19,27,0.3)]">-</span>}</td>}
                        {isListVisible('grossWeight') && <td className="px-4 py-4 text-right text-sm text-[#0E131B] tabular-nums">{entry.totalGrossWeight ? fmtNum(entry.totalGrossWeight) : <span className="text-[rgba(14,19,27,0.3)]">-</span>}</td>}
                        {isListVisible('tareWeight') && <td className="px-4 py-4 text-right text-sm text-[#0E131B] tabular-nums">{entry.totalTareWeight ? fmtNum(entry.totalTareWeight) : <span className="text-[rgba(14,19,27,0.3)]">-</span>}</td>}
                        {isListVisible('netWeight') && <td className="px-4 py-4 text-right text-sm font-semibold tabular-nums text-[#0E131B]">{entry.totalNetWeight ? fmtNum(entry.totalNetWeight) : <span className="text-[rgba(14,19,27,0.3)]">-</span>}</td>}
                        {isListVisible('location') && <td className="px-4 py-4 text-left text-sm text-[#0E131B]">{lotLocations(entry) || <span className="text-[rgba(14,19,27,0.3)]">-</span>}</td>}
                        {isListVisible('date') && <td className="px-4 py-4 text-center text-sm text-[#0E131B]">{fmtDate(entry.receivedDate)}</td>}
                        {isListVisible('status') && (
                        <td className="px-4 py-4 text-center">
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${statusBadge(entry.status)}`}>
                            {entry.status === 'in_stock' ? 'In Stock' : entry.status === 'partial' ? 'Partial' : 'Consumed'}
                          </span>
                        </td>
                        )}
                        {showCompanyColumn && (
                        <td className="px-4 py-4 text-left text-sm text-[#0E131B]">
                          <CompanyCell tenantId={entry.tenantId} />
                        </td>
                        )}
                        <td className="px-4 py-4 text-center" onClick={e => e.stopPropagation()}>
                          <button onClick={(e) => openActionMenu(e, entry.id)} className="p-1.5 rounded-lg text-[rgba(14,19,27,0.4)] hover:bg-[#F8F9FC] transition-colors">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><circle cx="10" cy="4" r="1.5" /><circle cx="10" cy="10" r="1.5" /><circle cx="10" cy="16" r="1.5" /></svg>
                          </button>
                        </td>
                      </motion.tr>
                      {/* Expanded carton rows */}
                      {expandedId === entry.id && entry.items?.length > 0 && (
                        <tr>
                          <td colSpan={visibleListColSpan} className="p-0">
                            <div className="bg-[#FAFBFD] border-b border-[#E8EBF1]">
                              <table className="min-w-full">
                                <thead>
                                  <tr className="bg-[#F0F2F8]">
                                    <th className="px-4 py-1.5 text-center text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">Sr.</th>
                                    <th className="px-4 py-1.5 text-center text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">CRTNNO</th>
                                    <th className="px-4 py-1.5 text-center text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">Twist</th>
                                    <th className="px-4 py-1.5 text-center text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">Chs.</th>
                                    <th className="px-4 py-1.5 text-right text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">GrWt</th>
                                    <th className="px-4 py-1.5 text-right text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">TrWt</th>
                                    <th className="px-4 py-1.5 text-right text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">NetWt</th>
                                    <th className="px-4 py-1.5 text-center text-[10px] font-semibold text-[rgba(14,19,27,0.5)] uppercase">Location</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {entry.items.sort((a: any, b: any) => a.srNo - b.srNo).map((item: any) => (
                                    <tr key={item.id} className="border-t border-[#E8EBF1]/50">
                                      <td className="px-4 py-1.5 text-center text-xs text-[rgba(14,19,27,0.6)]">{item.srNo}</td>
                                      <td className="px-4 py-1.5 text-center text-xs font-medium text-[#0E131B]">{item.crtnNo}</td>
                                      <td className="px-4 py-1.5 text-center text-xs text-[#0E131B]">{item.twist || '-'}</td>
                                      <td className="px-4 py-1.5 text-center text-xs text-[#0E131B]">{item.cheese || '-'}</td>
                                      <td className="px-4 py-1.5 text-right text-xs tabular-nums text-[#0E131B]">{fmtNum(item.grossWeight)}</td>
                                      <td className="px-4 py-1.5 text-right text-xs tabular-nums text-[#0E131B]">{fmtNum(item.tareWeight)}</td>
                                      <td className="px-4 py-1.5 text-right text-xs font-semibold tabular-nums text-emerald-700">{fmtNum(item.netWeight)}</td>
                                      <td className="px-4 py-1.5 text-center text-xs text-[#0E131B]">{item.location || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          ) : (
            <div className="p-16 text-center">
              <svg className="w-10 h-10 text-[rgba(14,19,27,0.15)] mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
              <p className="text-[rgba(14,19,27,0.4)] font-medium mb-3">No yarn entries found</p>
              <button onClick={openAdd} disabled={!canCreate} title={!canCreate ? 'You have read-only access' : undefined} className="px-4 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0B2278] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Add Yarn Entry</button>
            </div>
          )}

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-[#E8EBF1] flex-wrap gap-2">
              <span className="text-sm text-[rgba(14,19,27,0.5)]">{(page - 1) * pageSize + 1}-{Math.min(page * pageSize, pagination.total)} of {pagination.total}</span>
              <div className="flex items-center">
                <div className="inline-flex rounded-xl border border-[#E1E6EF] overflow-hidden">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                    className="px-3 py-1.5 text-sm text-[#0E131B] hover:bg-[#F8F9FC] disabled:opacity-30 disabled:cursor-not-allowed border-r border-[#E1E6EF] transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) pageNum = i + 1;
                    else if (page <= 3) pageNum = i + 1;
                    else if (page >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                    else pageNum = page - 2 + i;
                    return (
                      <button key={pageNum} onClick={() => setPage(pageNum)}
                        className={`px-3 py-1.5 text-sm font-medium border-r border-[#E1E6EF] last:border-r-0 transition-colors ${
                          page === pageNum ? 'bg-[#0055a9] text-white' : 'text-[#0E131B] hover:bg-[#F8F9FC]'
                        }`}>
                        {pageNum}
                      </button>
                    );
                  })}
                  <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page >= pagination.totalPages}
                    className="px-3 py-1.5 text-sm text-[#0E131B] hover:bg-[#F8F9FC] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-[rgba(14,19,27,0.5)]">
                <span>Show</span>
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="px-2 py-1 text-sm bg-white border border-[#E1E6EF] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]">
                  {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Action menu portal */}
        {actionMenuId !== null && createPortal(
          <div ref={actionMenuRef} className="fixed z-[9999] bg-white rounded-xl border border-[#E1E6EF] shadow-[0px_4px_16px_rgba(0,0,0,0.1)] py-1 w-44"
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}>
            {canUpdate && (
            <button
              onClick={() => { if (actionMenuId !== null) openEdit(actionMenuId); setActionMenuId(null); }}
              className="w-full flex items-center gap-2 text-left px-4 py-2 text-sm text-[#0E131B] hover:bg-[#F8F9FC] transition-colors">
              <svg className="w-4 h-4 text-[rgba(14,19,27,0.5)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              Edit
            </button>
            )}
            <button
              onClick={() => { setPrintEntryId(actionMenuId); setActionMenuId(null); }}
              className="w-full flex items-center gap-2 text-left px-4 py-2 text-sm text-[#0E131B] hover:bg-[#F8F9FC] transition-colors">
              <svg className="w-4 h-4 text-[rgba(14,19,27,0.5)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
              Print Labels
            </button>
            {canDelete && (
            <button
              onClick={() => { setDeletingId(actionMenuId); setActionMenuId(null); }}
              className="w-full text-left px-4 py-2 text-sm text-[#E02D3C] hover:bg-[#FEF2F2] transition-colors">
              Delete
            </button>
            )}
          </div>,
          document.body
        )}

        {/* Print box labels */}
        <YarnLabelPrint entryId={printEntryId} onClose={() => setPrintEntryId(null)} />

        {/* Delete Confirmation Modal */}
        {deletingId && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] p-4"
            tabIndex={-1} ref={el => el?.focus()}
            onKeyDown={e => { if (e.key === 'Escape') setDeletingId(null); if (e.key === 'Enter') { e.preventDefault(); deleteMutation.mutate(deletingId); setDeletingId(null); } }}>
            <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6">
              <h3 className="text-lg font-bold text-[#1B202A] mb-2">Delete Yarn Entry</h3>
              <p className="text-sm text-gray-500 mb-6">Are you sure? This will also delete all carton rows.</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeletingId(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                <button onClick={() => { deleteMutation.mutate(deletingId); setDeletingId(null); }}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors">
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}


        {/* List column chooser — controls only the list table (separate from the entry form's Field Settings) */}
        {showListSettings && createPortal(
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowListSettings(false)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8EBF1]">
                <div>
                  <h3 className="text-lg font-bold text-[#1B202A]">Table Columns</h3>
                  <p className="text-xs text-[rgba(14,19,27,0.5)]">Choose which columns show in the Yarn Inward list. Saved to your account.</p>
                </div>
                <button onClick={() => setShowListSettings(false)} className="p-1.5 rounded-lg hover:bg-[#F8F9FC] text-gray-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="px-5 py-4 overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                  {YM_LIST_COLUMNS.map(c => (
                    <label key={c.key} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${c.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${isListVisible(c.key) ? 'border-[#0055a9] bg-[#0055a9]/5' : 'border-[#E1E6EF] bg-white'}`}>
                      <input type="checkbox" checked={isListVisible(c.key)} disabled={c.required} onChange={() => toggleListColumn(c.key)}
                        className="rounded border-gray-300 text-[#0055a9] focus:ring-[#0055a9]" />
                      <span className="text-[#0E131B]">{c.label}{c.required && <span className="text-[10px] text-gray-400 ml-1">(required)</span>}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-t border-[#E8EBF1] bg-[#F8F9FC]">
                <button onClick={resetListColumns} className="text-sm font-medium text-gray-600 hover:text-[#0055a9]">Show all</button>
                <button onClick={() => setShowListSettings(false)} className="px-4 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780]">Done</button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {colourModalNode}
        {yarnNameModalNode}
      </motion.div>
    </>
  );
};

export default YarnMaster;
